import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Search,
  Save,
  ArrowLeft,
  Mail,
  Eye,
  EyeOff,
  Link2,
  MessageSquare,
  ImageIcon,
  Upload,
  Flag,
  Plus,
  ArrowUp,
  Settings,
  X,
  CheckCircle2
} from 'lucide-react';
import {
  APP_REGISTER,
  APP_REGISTER_DB,
  APP_GET_CLIENT,
  API_KEY,
  API_CLIENTS_NAME,
  REBRANDLY_APIKEY,
  MMS_GET_IMAGES,
  AWS_BUCKET_MMS,
  S3_GET_PRESIGNED_URLS,
  CHECK_USER_ENDPOINT,
  INVITE_USER_ENDPOINT
} from '../../utils/constants';
import { v4 as uuidv4 } from 'uuid';
import AppRegisterSkeleton from './AppRegisterSkeleton';
import { useUser } from '@clerk/clerk-react';
import { setSelectedClient } from '../../store/slices/selectedClient';

const axios = require('axios');

const AppRegisterPage = () => {
  const { user } = useUser();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedClient = useSelector(
    (state) => state.selectedClient.selectedClient
  );
  const { customerList } = useSelector((state) => state.clients);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const [clientInfo, setClientInfo] = useState({});
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('form-control');
  const [passwordStatus, setPasswordStatus] = useState('form-control');
  const [messageStatus, setMessageStatus] = useState('form-control');
  const [password, setPassword] = useState('myreviewapp');
  const [password2, setPassword2] = useState('myreviewapp');
  const [message, setMessage] = useState(
    '#name, we appreciate you choosing #company. Please help us spread the word about our services by using the review links below.'
  );
  const [copyData, setCopyData] = useState(null);
  const [clientImages, setClientImages] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  const [files, setFiles] = useState({ length: 0 });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [gmbLink, setGmbLink] = useState({ long: '', short: '' });
  const [yelpLink, setYelpLink] = useState({ long: '', short: '' });
  const [featureFlags, setFeatureFlags] = useState({
    notifications: true
  });
  const [fullAccess, setFullAccess] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [longGmb, setLongGmb] = useState(null);
  const [longYelp, setLongYelp] = useState(null);
  const [typePass, setTypePass] = useState('password');
  const [gmbShortId, setGmbShortId] = useState('');
  const [yelpShortId, setYelpShortId] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [userNotFoundModalOpen, setUserNotFoundModalOpen] = useState(false);
  const userNotFoundResolverRef = useRef(null);

  const resolveUserNotFoundAction = (action) => {
    if (userNotFoundResolverRef.current) {
      userNotFoundResolverRef.current(action);
      userNotFoundResolverRef.current = null;
    }
    setUserNotFoundModalOpen(false);
  };

  const askUserNotFoundAction = () =>
    new Promise((resolve) => {
      userNotFoundResolverRef.current = resolve;
      setUserNotFoundModalOpen(true);
    });

  // Get customer list for search
  const getCustomerList = () => {
    if (!customerList || customerList.length === 0) return [];
    return customerList.map((client) => ({
      ...client,
      value: client.clientName,
      label: client.clientName
    }));
  };

  // Handle client change from search
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }
  };

  // Redirect if no client is selected
  useEffect(() => {
    if (!selectedClient || !selectedClient.id) {
      history.push('/');
    }
  }, [selectedClient, history]);

  // ===============  SET CLIENT INFO ================
  useEffect(() => {
    if (selectedClient?.appActive && selectedClient?.appUid) {
      const getClient = async (uid) => {
        setLoadingData(true);
        let res = await axios
          .get(`${APP_GET_CLIENT}?uid=${uid}`, {
            headers: {
              'X-API-KEY': `${API_KEY}`
            }
          })
          .then(async (res) => {
            return res;
          })
          .catch((err) => console.error(err));
        if (res.status === 200) {
          const body = res?.data?.body?.[0] || {};
          const {
            email = '',
            gmbLongLink = '',
            gmbShortLink = '',
            yelpLongLink = '',
            yelpShortLink = '',
            message = '',
            featureFlags = { notifications: true },
            fullAccess = false,
            selectedImage: selImage = null
          } = body;

          if (selImage) {
            setSelectedImage(selImage);
          }
          if (gmbShortLink) {
            let shortlinkgmb = gmbShortLink.split('/');
            if (shortlinkgmb.length > 1) {
              let idGmb = await axios.get(
                'https://api.rebrandly.com/v1/links?slashtag=' +
                  shortlinkgmb[1] +
                  '&domain.fullName=' +
                  shortlinkgmb[0],
                {
                  headers: {
                    'Content-Type': 'application/json',
                    apikey: REBRANDLY_APIKEY
                  }
                }
              );
              if (idGmb.data?.[0]?.id) setGmbShortId(idGmb.data[0].id);
            }
          }
          if (yelpShortLink) {
            let shortlinkyelp = yelpShortLink.split('/');
            if (shortlinkyelp.length > 1) {
              let idYelp = await axios.get(
                'https://api.rebrandly.com/v1/links?slashtag=' +
                  shortlinkyelp[1] +
                  '&domain.fullName=' +
                  shortlinkyelp[0],
                {
                  headers: {
                    'Content-Type': 'application/json',
                    apikey: REBRANDLY_APIKEY
                  }
                }
              );
              if (idYelp.data?.[0]?.id) setYelpShortId(idYelp.data[0].id);
            }
          }
          setGmbLink({ long: gmbLongLink, short: gmbShortLink });
          setYelpLink({ long: yelpLongLink, short: yelpShortLink });
          setLong({ gmb: gmbLongLink, yelp: yelpLongLink });
          setMessage(message);
          setEmail(email);
          setClientInfo({
            ...selectedClient,
            clientName: selectedClient?.clientName || '',
            email
          });
          setFeatureFlags(featureFlags);
          setFullAccess(fullAccess);
        } else {
          setClientInfo({
            ...selectedClient,
            clientName: selectedClient?.clientName || '',
            email: selectedClient?.email || ''
          });
        }
        setLoadingData(false);
      };
      getClient(selectedClient.appUid);
    } else {
      setClientInfo({
        ...selectedClient,
        clientName: selectedClient?.clientName || '',
        email: selectedClient?.email || ''
      });
    }
  }, [selectedClient]);

  // =============== UPLOAD IMAGES==============
  const renderImages = (files) => {
    let i = 0;

    let itemsList = [];
    while (i < files.length) {
      itemsList[i] = files[i];

      i++;
    }
    itemsList = itemsList.map((item, index) => {
      function returnFileSize(number) {
        if (number < 1024) {
          return number + 'bytes';
        } else if (number >= 1024 && number < 1048576) {
          return (number / 1024).toFixed(1) + 'KB';
        } else if (number >= 1048576) {
          return (number / 1048576).toFixed(1) + 'MB';
        }
      }
      function handleName(name) {
        if (name.length > 35) {
          return name.slice(0, 35) + ' ...';
        } else return name;
      }
      return (
        <div
          key={index}
          className="card"
          style={{
            width: '48%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: 5,
              marginTop: 5,
              overflow: 'hidden'
            }}>
            <b>FileName: </b>
            <u>{handleName(item.name)}</u>
            <b>Size: </b>
            {returnFileSize(item.size)}
          </div>
          <div>
            <img
              alt=""
              src={URL.createObjectURL(item)}
              style={{ width: 100, height: 100, objectFit: 'cover' }}
            />
          </div>
        </div>
      );
    });

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          flexFlow: 'row wrap',
          alignContent: 'flex-end',
          justifyContent: 'space-between'
        }}>
        {itemsList}
      </div>
    );
  };

  // ============== GET IMAGES =================
  const getImages = async (get = false) => {
    let imgs = copyData;
    if (copyData === null || get) {
      imgs = await axios.get(MMS_GET_IMAGES + `userId=${selectedClient.id}`);
      if (imgs.data.body === 'empty') {
        setSelectedImage(null);
        setClientImages(null);
        return;
      }
      imgs = imgs.data.body;
      setCopyData(imgs);
    }
    let copy2 = imgs;
    imgs = imgs.map((image, index) => {
      let url = image.url;
      return (
        <div
          key={index}
          onClick={() => {
            if (url === selectedImage) {
              setSelectedImage(null);
            } else {
              setSelectedImage(image.url);
            }
          }}
          style={{
            width: '49%',
            display: 'flex',
            flexDirection: 'row',

            border:
              url === selectedImage
                ? '5px solid #567CC8'
                : '5px solid transparent',
            filter: url === selectedImage ? 'grayscale(0)' : 'grayscale(1)',
            transition: '200ms'
          }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              zIndex: 100
            }}>
            <div
              style={{
                position: 'relative',
                top: -8,
                right: -7,
                zIndex: 100,
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
              {url === selectedImage ? (
                <svg version="1.1" width="15" height="15" viewBox="0 0 508 508">
                  <g>
                    <g>
                      <path
                        d="m508 254c0 16.23-8.43 29.71-15.87 41.59-4.4 7.04-8.56 13.68-10.12 19.5-1.67 6.27-1.41 14.38-1.13 22.97.45 13.79.96 29.42-6.89 42.97-7.95 13.75-21.85 21.14-34.11 27.66-7.49 3.98-14.56 7.74-19 12.19-4.45 4.44-8.21 11.51-12.19 19-6.52 12.26-13.91 26.16-27.66 34.12-13.55 7.84-29.18 7.33-42.97 6.88-8.59-.28-16.7-.54-22.97 1.13-5.81 1.56-12.46 5.72-19.5 10.12-11.88 7.44-25.36 15.87-41.59 15.87h-.11l-35.355-262.45 35.355-245.55h.11c16.23 0 29.71 8.43 41.59 15.87 7.04 4.4 13.69 8.56 19.5 10.12 6.27 1.67 14.38 1.41 22.97 1.13 13.79-.45 29.42-.96 42.97 6.88 13.75 7.96 21.14 21.86 27.66 34.12 3.98 7.49 7.74 14.56 12.19 19 4.44 4.45 11.51 8.21 19 12.19 12.26 6.52 26.16 13.91 34.12 27.66 7.84 13.55 7.33 29.18 6.88 42.97-.28 8.59-.54 16.7 1.13 22.97 1.56 5.81 5.72 12.46 10.12 19.49 7.44 11.89 15.87 25.37 15.87 41.6z"
                        fill="#7da7ff"
                        data-original="#00cb76"
                      />
                      <path
                        d="m253.89 0v508c-16.18-.04-29.63-8.45-41.48-15.87-7.04-4.4-13.69-8.56-19.5-10.12-6.27-1.67-14.38-1.41-22.97-1.13-13.79.45-29.42.96-42.97-6.88-13.75-7.96-21.14-21.86-27.66-34.12-3.98-7.49-7.74-14.56-12.19-19-4.44-4.45-11.51-8.21-19-12.19-12.26-6.52-26.16-13.91-34.12-27.66-7.84-13.55-7.33-29.18-6.88-42.97.28-8.59.54-16.7-1.13-22.97-1.56-5.81-5.72-12.46-10.12-19.49-7.44-11.89-15.87-25.37-15.87-41.6s8.43-29.71 15.87-41.59c4.4-7.04 8.56-13.68 10.12-19.5 1.67-6.27 1.41-14.38 1.13-22.97-.45-13.79-.96-29.42 6.89-42.97 7.95-13.75 21.85-21.14 34.11-27.66 7.49-3.98 14.56-7.74 19-12.19 4.45-4.44 8.21-11.51 12.19-19 6.52-12.26 13.91-26.16 27.66-34.12 13.55-7.84 29.18-7.33 42.97-6.88 8.59.28 16.7.54 22.97-1.13 5.81-1.56 12.46-5.72 19.5-10.12 11.85-7.42 25.3-15.83 41.48-15.87z"
                        fill="#7da7ff"
                        data-original="#a4e786"
                      />
                      <path
                        d="m445.08 254c0 105.36-85.72 191.08-191.08 191.08h-.11l-7.39-10.724 7.39-19.276h.11c88.82 0 161.08-72.26 161.08-161.08s-72.26-161.08-161.08-161.08h-.11l-12.543-12.148 12.543-17.852h.11c105.36 0 191.08 85.72 191.08 191.08z"
                        fill="#ffffff"
                        data-original="#009a85"
                      />
                      <path
                        d="m344.39 212.93-90.5 86.88-7.39-15.81 7.39-25.78 69.72-66.93z"
                        fill="#ffffff"
                        data-original="#009a85"
                      />
                      <g fill="#00cb76">
                        <path
                          d="m253.89 258.22v41.59l-28.21 27.08-62.29-62.28 21.22-21.22 41.5 41.5z"
                          fill="#ffffff"
                          data-original="#00cb76"
                        />
                        <path
                          d="m253.89 92.92v-30c-105.31.06-190.97 85.76-190.97 191.08s85.66 191.02 190.97 191.08v-30c-88.77-.06-160.97-72.3-160.97-161.08s72.2-161.02 160.97-161.08z"
                          fill="#ffffff"
                          data-original="#00cb76"
                        />
                      </g>
                    </g>
                  </g>
                </svg>
              ) : null}
            </div>
          </div>

          <img
            style={{
              backgroundRepeat: 'no-repeat',
              width: '100%',
              height: 110,
              objectFit: 'cover'
            }}
            alt={image.url}
            src={image.url}
          />
        </div>
      );
    });
    setCopyData(copy2);
    setClientImages(
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          flexFlow: 'row wrap',
          alignContent: 'flex-end',
          justifyContent: 'space-between'
        }}>
        {imgs}
      </div>
    );
    return;
  };

  useEffect(() => {
    getImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);

  // ============================================
  const setLong = ({ gmb: gmbLink, yelp: yelpLink }) => {
    setLongGmb(gmbLink);
    setLongYelp(yelpLink);
  };

  // ================ GET REBRANDLY ====================
  const generateShortLink = async (url, link) => {
    let payload = {
      description: clientInfo.clientName,
      destination: url,
      domain: { fullName: link }
    };
    let axiosConfig = {
      headers: {
        'Content-Type': 'application/json',
        apikey: REBRANDLY_APIKEY
      }
    };

    let res = await axios
      .post('https://api.rebrandly.com/v1/links', payload, axiosConfig)
      .then((response) => {
        if (link === 'google-review.link') {
          return response.data;
        } else {
          return response.data;
        }
      })
      .catch((error) => {
        console.error(error);
      });
    return res;
  };

  // ==================== Check format URL ============================
  const createShortLinks = async () => {
    const urlRegexp =
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
    const regex = new RegExp(urlRegexp);
    let gmbShort = '';
    let yelpShort = '';
    let data = '';
    let gmbId = '';
    let yelpId = '';

    if (gmbLink.long) {
      if (gmbLink.long.match(regex)) {
        if (longGmb !== gmbLink.long) {
          const reviewLink = 'google-review.link';

          data = await generateShortLink(gmbLink.long, reviewLink);
          gmbShort = data.shortUrl;
          gmbId = data.id;
          setGmbLink({ ...gmbLink, short: data.shortUrl });
        } else {
          gmbShort = gmbLink.short;
          setGmbLink({ ...gmbLink, short: gmbShort });
          gmbId = gmbShortId;
        }
      } else {
        Swal.fire('Oops!', '<span> Invalid gmb link </span>');
        setSavingClient(false);
        return;
      }
    }
    let formatedLink = null;
    if (yelpLink.long) {
      if (yelpLink.long.match(regex)) {
        if (longYelp !== yelpLink.long) {
          const reviewLink = 'yelp-review.link';
          formatedLink = yelpLink.long?.includes('&rating=5')
            ? yelpLink.long
            : yelpLink.long + '&rating=5';
          data = await generateShortLink(formatedLink, reviewLink);
          yelpShort = data.shortUrl;
          yelpId = data.id;
          setYelpLink({
            short: data.shortUrl,
            long: formatedLink
          });
          setLongYelp(formatedLink);
        } else {
          yelpShort = yelpLink.short;
          setYelpLink({ ...yelpLink, short: yelpShort });
          yelpId = yelpShortId;
        }
      } else {
        Swal.fire('Oops!', '<span> Invalid yelp link. </span>');
        setSavingClient(false);
        return;
      }
    }
    saveClient(gmbShort, yelpShort, gmbId, yelpId, formatedLink);
  };

  // =================== SAVE CLIENT ====================
  const saveClient = async (
    gmbShort,
    yelpShort,
    gmbId,
    yelpId,
    yelpLongLink
  ) => {
    let axiosConfig = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    const payload = {
      email,
      password,
      message,
      id: clientInfo.id,
      company: clientInfo.clientName,
      gmbLongLink: gmbLink.long,
      gmbShortLink: gmbShort,
      yelpLongLink: yelpLongLink || yelpLink.long,
      yelpShortLink: yelpShort,
      gmbId,
      yelpId,
      featureFlags,
      fullAccess,
      selectedImage
    };

    const submitAppRegister = async () => {
      try {
        const response = await axios.post(APP_REGISTER, payload, axiosConfig);
        const { statusCode, body } = response.data;

        if (statusCode === 201) {
          Swal.fire('Error!', '<span>' + body + '</span>');
          setSavingClient(false);
          return false;
        }

        if (statusCode === 200) {
          if (body?.uid) {
            dispatch(
              setSelectedClient({
                ...selectedClient,
                appUid: body.uid,
                appActive: true
              })
            );
          }
          setSavingClient(false);
          setSuccessModalOpen(true);
          return true;
        }

        Swal.fire('Error!', '<span>' + body + '</span>');
        setSavingClient(false);
        return false;
      } catch (error) {
        console.error('error', error);
        setSavingClient(false);
        return false;
      }
    };

    try {
      // Step 1: Check if user exists
      const checkUserResponse = await axios.post(
        CHECK_USER_ENDPOINT,
        { email },
        axiosConfig
      );

      const { userExists } = checkUserResponse.data;

      if (userExists) {
        // User exists - proceed with normal registration flow
        await submitAppRegister();
      } else {
        // User doesn't exist - ask user action in custom modal
        const action = await askUserNotFoundAction();

        if (action === 'createWithoutInvite') {
          // Run same flow as "User exists" without invitation endpoint
          await submitAppRegister();
          return;
        }

        // Only send invitation if user confirms
        if (action === 'sendInvite') {
          try {
            // Step 1: Create user in database first
            const dbResponse = await axios.post(
              APP_REGISTER_DB,
              payload,
              axiosConfig
            );

            // Check HTTP status, not body.statusCode
            if (dbResponse.status !== 200) {
              const errorMessage =
                dbResponse.data || 'Failed to create user in database';
              Swal.fire('Error!', `<span>${errorMessage}</span>`);
              setSavingClient(false);
              return;
            }

            // Step 2: Send Clerk invitation
            const inviteResponse = await axios.post(
              INVITE_USER_ENDPOINT,
              { email },
              axiosConfig
            );

            if (inviteResponse.status === 200) {
              Swal.fire(
                'Invitation Sent!',
                `<span>An invitation has been sent to ${email}. Once they accept and create their account, you can configure their settings.</span>`,
                'success'
              );
              setSavingClient(false);
            }
          } catch (error) {
            // Handle errors from either DB creation or invitation
            if (
              error.response?.status === 400 &&
              error.config?.url === INVITE_USER_ENDPOINT
            ) {
              const errorData = error.response.data;
              if (errorData?.error?.errors?.[0]?.code === 'duplicate_record') {
                Swal.fire(
                  'Invitation Already Sent',
                  `<span>There is already a pending invitation for ${email}. Please ask the user to check their email.</span>`,
                  'warning'
                );
              } else {
                Swal.fire(
                  'Error!',
                  '<span>Failed to send invitation. Please try again.</span>',
                  'error'
                );
              }
            } else {
              const errorMessage =
                error.response?.data?.body ||
                error.message ||
                'An error occurred';
              Swal.fire('Error!', `<span>${errorMessage}</span>`, 'error');
            }
            setSavingClient(false);
          }
        } else {
          // User cancelled - don't send invitation
          setSavingClient(false);
        }
      }
    } catch (error) {
      console.error('Error checking user existence:', error);
      Swal.fire(
        'Error!',
        '<span>Failed to verify user. Please try again.</span>',
        'error'
      );
      setSavingClient(false);
    }
  };

  const verifyForm = async (email, password, password2) => {
    function ValidateEmail(inputText) {
      let mailformat =
        // eslint-disable-next-line no-control-regex
        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
      const regex = new RegExp(mailformat);
      if (inputText.match(regex)) {
        return true;
      } else {
        return false;
      }
    }
    if (!loadingData) {
      setSavingClient(true);
      if (email.length >= 6 && ValidateEmail(email)) {
        setEmailStatus('form-control');
        if (password.length >= 8) {
          if (password === password2) {
            if (message.length >= 10) {
              createShortLinks();
            } else {
              Swal.fire(
                'Oops!',
                '<span> The message must containe at least 10 characters. </span>'
              ).then(() => {
                setSavingClient(false);
                setMessageStatus('form-control error-field');
              });
            }
          } else {
            Swal.fire('Ups!', "<span> Passwords don't match </span>").then(
              () => {
                setSavingClient(false);
                setPasswordStatus('form-control error-field');
              }
            );
          }
        } else {
          setSavingClient(false);
          Swal.fire(
            'Ups!',
            '<span> Your password must contain at least 8 characters </span>'
          ).then(() => {
            setPasswordStatus('form-control error-field');
          });
        }
      } else {
        Swal.fire('Ups!', '<span> Check the <b>email</b> address.</span>').then(
          () => {
            setEmailStatus('form-control error-field');
          }
        );
        setSavingClient(false);
      }
    }
  };

  const handleChangePass = (event) => {
    setPassword(event.target.value);
    setPasswordStatus('form-control');
  };

  const handleChangePass2 = (event) => {
    setPassword2(event.target.value);
    setPasswordStatus('form-control');
  };

  const handleChangeEmail = (event) => {
    setEmail(event.target.value);
    setEmailStatus('form-control');
  };

  const handleChangeMessage = (event) => {
    setMessage(event.target.value);
    setMessageStatus('form-control');
  };

  const changeTypePass = () => {
    if (typePass === 'password') {
      setTypePass('text');
    } else {
      setTypePass('password');
    }
  };

  const handleUpload = async (file) => {
    const fileType = file.type;
    const fileName = `${uuidv4()}.${file.name.split('.').pop()}`;

    try {
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: AWS_BUCKET_MMS,
          files: [{ fileName: `${clientInfo.id}/${fileName}`, fileType }]
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get presigned URL');
      }

      const { urls } = await presignedResponse.json();
      const presignedUrl = urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No presigned URL returned');
      }

      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': fileType
        },
        body: file
      });

      if (!uploadResult.ok) {
        throw new Error('File upload to S3 failed');
      }

      const uploadedFileUrl = `https://${AWS_BUCKET_MMS}.s3.amazonaws.com/${clientInfo.id}/${fileName}`;
      console.log(`File uploaded successfully: ${uploadedFileUrl}`);
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to upload image', 'error');
    }
  };

  // Build preview message

  const previewMessage = message
    .replace('#name', 'John')
    .replace('#company', clientInfo?.clientName || 'Your Company');

  if (!selectedClient || !selectedClient.id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">No client selected</h2>
          <p className="text-base text-muted-foreground">
            Please select a client from the dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (loadingData) {
    return <AppRegisterSkeleton />;
  }

  return (
    <div className="flex min-h-screen bg-muted/30">
      <div className="flex-1">
        <main className="p-3 sm:p-6 lg:p-10 space-y-6 sm:space-y-8">
          {/* Page Title */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div className="flex items-center gap-3 sm:gap-4">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex-shrink-0"
                onClick={() => history.push('/')}>
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-foreground">
                  {' '}
                  SMS & App Settings, {clientInfo?.clientName}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  {loadingData
                    ? 'Loading...'
                    : 'Manage login credentials, review links, and SMS messaging'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-wrap w-full sm:w-auto">
              {isSuperAdmin && (
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <Select
                    placeholder="Search clients..."
                    onChange={(client) => handleChangeClient(client)}
                    options={getCustomerList()}
                    hideSelectedOptions={true}
                    controlShouldRenderValue={false}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided, state) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        borderWidth: '1px',
                        borderColor: state.isFocused
                          ? 'hsl(var(--primary))'
                          : 'hsl(var(--border))',
                        boxShadow: state.isFocused
                          ? '0 0 0 1px hsl(var(--primary))'
                          : 'none',
                        backgroundColor: '#ffffff',
                        paddingTop: '10px',
                        paddingBottom: '9px',
                        paddingLeft: '2.5rem',
                        fontSize: '1rem',
                        '&:hover': {
                          borderColor: 'hsl(var(--primary))'
                        }
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'hsl(var(--muted-foreground))',
                        fontSize: '1rem'
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: '1rem',
                        color: 'hsl(var(--foreground))'
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                        boxShadow:
                          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                        color: state.isFocused ? '#1e293b' : '#64748b',
                        cursor: 'pointer',
                        padding: '12px 16px',
                        fontSize: '1rem'
                      })
                    }}
                  />
                </div>
              )}
              <Button
                size="lg"
                disabled={savingClient || uploadingImages || loadingData}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 px-4 sm:px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-2 sm:gap-3 w-full sm:w-auto"
                onClick={() => verifyForm(email, password, password2)}>
                <Save className="h-5 w-5" />
                {savingClient ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Account Information */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Settings className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl lg:text-2xl font-bold">
                      Account Information
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      Client credentials and review links
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 px-4 sm:px-6 lg:px-8 pb-8">
                {/* Client's Email */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Client Email
                  </Label>
                  <Input
                    value={email}
                    onChange={handleChangeEmail}
                    disabled={loadingData}
                    className={`h-12 text-base rounded-xl border-2 focus:border-primary px-4 ${
                      emailStatus.includes('error') ? 'border-destructive' : ''
                    }`}
                  />
                </div>

                {/* Password / Repeat Password */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Password</Label>
                    <div className="relative">
                      <Input
                        type={typePass}
                        value={password}
                        onChange={handleChangePass}
                        disabled={loadingData}
                        className={`h-12 text-base rounded-xl border-2 focus:border-primary px-4 pr-12 ${
                          passwordStatus.includes('error')
                            ? 'border-destructive'
                            : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={changeTypePass}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {typePass !== 'text' ? (
                          <Eye className="h-5 w-5" />
                        ) : (
                          <EyeOff className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      Repeat Password
                    </Label>
                    <div className="relative">
                      <Input
                        type={typePass}
                        value={password2}
                        onChange={handleChangePass2}
                        disabled={loadingData}
                        className={`h-12 text-base rounded-xl border-2 focus:border-primary px-4 pr-12 ${
                          passwordStatus.includes('error')
                            ? 'border-destructive'
                            : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={changeTypePass}
                        disabled={loadingData}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {typePass !== 'text' ? (
                          <Eye className="h-5 w-5" />
                        ) : (
                          <EyeOff className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Google Link */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    Google Review Link
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={gmbLink.long}
                      onChange={(e) =>
                        setGmbLink({ ...gmbLink, long: e.target.value })
                      }
                      disabled={loadingData}
                      className="h-12 text-base rounded-xl border-2 focus:border-primary px-4 flex-1"
                    />
                    <div
                      className={
                        gmbLink?.short?.length > 0
                          ? 'text-green-500'
                          : 'text-red-500'
                      }>
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                {/* Yelp Link */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    Yelp Review Link
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={yelpLink.long}
                      onChange={(e) =>
                        setYelpLink({ ...yelpLink, long: e.target.value })
                      }
                      disabled={loadingData}
                      autoComplete="nope"
                      className="h-12 text-base rounded-xl border-2 focus:border-primary px-4 flex-1"
                    />
                    <div
                      className={
                        yelpLink?.short?.length > 0
                          ? 'text-green-500'
                          : 'text-red-500'
                      }>
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                {/* Short Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      Short Link (Google)
                    </Label>
                    <Input
                      value={gmbLink.short}
                      readOnly
                      className="h-12 text-base rounded-xl border-2 px-4 bg-muted/50 text-muted-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      Short Link (Yelp)
                    </Label>
                    <Input
                      value={yelpLink.short}
                      readOnly
                      className="h-12 text-base rounded-xl border-2 px-4 bg-muted/50 text-muted-foreground"
                    />
                  </div>
                </div>

                {/* Feature Flags */}
                <div className="space-y-3 pt-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Flag className="h-4 w-4 text-muted-foreground" />
                    Feature Settings
                  </Label>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-base">Notifications</span>
                    <Switch
                      checked={featureFlags?.notifications}
                      disabled={loadingData}
                      onCheckedChange={(checked) => {
                        setFeatureFlags({
                          ...featureFlags,
                          notifications: checked
                        });
                      }}
                      className="data-[state=checked]:bg-primary"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Message Configuration */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl lg:text-2xl font-bold">
                      Review Request Message
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      Customize review request messages
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 lg:px-8 pb-8">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* iPhone Preview */}
                  <div className="flex justify-center lg:justify-start shrink-0">
                    <div className="relative w-[260px]">
                      <div className="bg-foreground rounded-[3rem] p-[10px] shadow-2xl">
                        <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[100px] h-[24px] bg-foreground rounded-b-2xl z-10 flex items-center justify-center">
                          <div className="w-[50px] h-[4px] bg-muted-foreground/30 rounded-full" />
                        </div>
                        <div className="bg-background rounded-[2.4rem] overflow-hidden">
                          <div className="h-10" />
                          <div className="bg-primary/5 border-b border-border px-4 py-2.5">
                            <p className="text-xs font-semibold text-foreground text-center">
                              Messages
                            </p>
                          </div>
                          <div className="px-3 py-4 min-h-[380px] bg-background">
                            <div className="flex justify-start mb-2">
                              <div className="bg-primary/10 rounded-2xl rounded-bl-md px-3 py-2.5 max-w-[90%]">
                                <p className="text-[11px] text-foreground leading-relaxed">
                                  {previewMessage}
                                </p>
                                {selectedImage && (
                                  <img
                                    src={selectedImage}
                                    alt="preview"
                                    className="mt-2 rounded-lg max-w-full h-auto"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-border px-2.5 py-2 flex items-center gap-1.5 bg-muted/30">
                            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                              <Plus className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div className="flex-1 bg-background border border-border rounded-full px-3 py-1.5">
                              <span className="text-[10px] text-muted-foreground">
                                iMessage
                              </span>
                            </div>
                            <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                              <ArrowUp className="h-3 w-3 text-primary-foreground" />
                            </div>
                          </div>
                          <div className="flex justify-center py-1.5">
                            <div className="w-[80px] h-[3px] bg-muted-foreground/30 rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inputs */}
                  <div className="flex-1 space-y-6">
                    {/* Custom Message */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">
                        Custom Message
                      </Label>
                      <Textarea
                        value={message}
                        onChange={handleChangeMessage}
                        disabled={loadingData}
                        className={`min-h-[220px] text-base rounded-xl border-2 focus:border-primary p-4 resize-none ${
                          messageStatus.includes('error')
                            ? 'border-destructive'
                            : ''
                        }`}
                      />
                    </div>

                    {/* MMS Section */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        Attach an Image (MMS)
                      </Label>
                      <div className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center min-h-[120px] bg-muted/20">
                        {loadingData ? (
                          <p className="text-sm text-muted-foreground">
                            Loading images...
                          </p>
                        ) : clientImages ? (
                          <div className="w-full max-h-[200px] overflow-y-auto">
                            {clientImages}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No images
                          </p>
                        )}
                      </div>
                      <label htmlFor="image_uploads">
                        <Button
                          variant="outline"
                          className="w-full gap-2 h-11 rounded-xl text-base cursor-pointer"
                          asChild
                          disabled={
                            uploadingImages || savingClient || loadingData
                          }>
                          <span>
                            <Upload className="h-4 w-4" />
                            Add Images
                          </span>
                        </Button>
                      </label>
                      <input
                        id="image_uploads"
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        className="hidden"
                        multiple
                        disabled={uploadingImages}
                        onChange={(e) => {
                          setFiles(e.target.files);
                        }}
                      />
                      <p className="text-sm text-destructive font-medium">
                        Only .jpg/.png images will be accepted
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Management */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    User Management
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Control the access level for this client
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 lg:px-8 pb-8">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-base font-semibold text-foreground">
                    Access Level
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {fullAccess ? 'Full Access' : ' View Only'}
                  </p>
                </div>
                <Switch
                  checked={fullAccess}
                  disabled={loadingData}
                  onCheckedChange={(checked) => setFullAccess(checked)}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bottom Actions */}
          <div className="pt-4 pb-8 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
            <Button
              variant="outline"
              size="lg"
              disabled={savingClient || uploadingImages}
              className="h-14 px-8 text-lg font-semibold rounded-xl"
              onClick={() => history.push('/')}>
              Close
            </Button>
            <Button
              size="lg"
              disabled={savingClient || uploadingImages || loadingData}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
              onClick={() => verifyForm(email, password, password2)}>
              <Save className="h-5 w-5" />
              {savingClient ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </main>
      </div>

      {/* Upload Images Modal */}
      {files.length > 0 && (
        <Dialog
          open={files.length > 0}
          onOpenChange={() => setFiles({ length: 0 })}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                Upload Images ({files.length})
              </DialogTitle>
              <DialogDescription>
                Select images to upload to the client gallery
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto p-4">
              {renderImages(files)}
            </div>
            <div className="flex justify-end gap-4 pt-4">
              <Button
                variant="outline"
                disabled={savingClient || uploadingImages}
                onClick={() => setFiles({ length: 0 })}>
                Cancel
              </Button>
              <Button
                disabled={savingClient || uploadingImages || files.length === 0}
                onClick={async () => {
                  setUploadingImages(true);
                  setProgress(0);

                  for (let i = 0; i < files.length; i++) {
                    await handleUpload(files[i]);
                    const progressPercentage = Math.round(
                      ((i + 1) / files.length) * 100
                    );
                    setProgress(progressPercentage);
                  }

                  setTimeout(async () => {
                    setUploadingImages(false);
                    setFiles({ length: 0 });
                    setProgress(0);
                    setCopyData(null);
                    getImages(true);
                  }, 500);
                }}>
                {uploadingImages
                  ? `Uploading... ${progress !== 0 ? progress : '0'}%`
                  : 'Upload'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Settings saved successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => {
                setSuccessModalOpen(false);
              }}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Not Found Modal */}
      <Dialog
        open={userNotFoundModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            resolveUserNotFoundAction('cancel');
          }
        }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>User Not Found</DialogTitle>
            <DialogDescription>
              The email {email} is not registered in the system. Choose how you
              want to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2 lg:flex-row lg:flex-wrap lg:justify-end">
            <Button
              variant="outline"
              className="w-full lg:w-auto whitespace-normal text-center"
              onClick={() => resolveUserNotFoundAction('cancel')}>
              Cancel
            </Button>
            <Button
              variant="outline"
              className="w-full lg:w-auto whitespace-normal text-center"
              onClick={() => resolveUserNotFoundAction('createWithoutInvite')}>
              Create User Without Invitation
            </Button>
            <Button
              className="w-full lg:w-auto whitespace-normal text-center"
              onClick={() => resolveUserNotFoundAction('sendInvite')}>
              Yes, Send Invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppRegisterPage;
